import React, { useState, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { doc, updateDoc, increment } from 'firebase/firestore';
import { db } from '../../firebase';
import { paymentsService } from '../../services/paymentsService';

export default function EditOrderModal({ isOpen, onClose, order, onOrderUpdated }) {
  const [formData, setFormData] = useState({
    amountReceived: '',
    notes: order?.notes || ''
  });
  const [loading, setLoading] = useState(false);
  const [discount, setDiscount] = useState(order.discount?.toString() || '0');

  const currentBalance = order.totalAmount - (order.amountReceived || 0);

  const calculateTotals = () => {
    const subtotal = order.items.reduce((sum, item) => {
      return sum + (item.actualQuantity * item.pricePerKg || 0);
    }, 0);
    
    const total = subtotal - Number(discount);
    return { subtotal, total };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const orderRef = doc(db, 'orders', order.id);
      const { subtotal, total } = calculateTotals();
      const newAmountReceived = Number(order.amountReceived || 0) + Number(formData.amountReceived || 0);
      const newRemainingBalance = total - newAmountReceived;
      
      const oldTotal = order.totalAmount;
      const balanceAdjustment = oldTotal - total;
      const paymentAmount = Number(formData.amountReceived || 0);

      const updateData = {
        amountReceived: newAmountReceived,
        remainingBalance: newRemainingBalance,
        notes: formData.notes,
        discount,
        subtotal,
        totalAmount: total,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(orderRef, updateData);
      
      const customerRef = doc(db, 'contacts', order.customerId);
      const totalBalanceAdjustment = paymentAmount + balanceAdjustment;
      
      await updateDoc(customerRef, {
        totalPaid: increment(paymentAmount),
        balance: increment(-totalBalanceAdjustment),
        updatedAt: new Date().toISOString()
      });

      // Record the payment
      if (paymentAmount > 0) {
        await paymentsService.recordPayment({
          customerId: order.customerId,
          customerName: order.customerName,
          orderId: order.id,
          amount: paymentAmount,
          type: 'order_payment',
          notes: formData.notes,
          orderDetails: {
            items: order.items,
            totalAmount: total,
            remainingBalance: newRemainingBalance
          }
        });
      }

      onOrderUpdated({
        ...order,
        ...updateData
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Failed to update order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25 dark:bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-end sm:items-center justify-center p-0 sm:p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full sm:max-w-md transform overflow-hidden bg-white dark:bg-gray-800 
                p-4 sm:p-6 text-left align-middle shadow-xl transition-all rounded-t-2xl sm:rounded-2xl">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white"
                  >
                    Update Order Details
                  </Dialog.Title>
                  <button
                    type="button"
                    className="text-gray-400 hover:text-gray-500 sm:hidden"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 mb-4 space-y-2">
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Customer:</span>
                    <span className="font-medium">{order.customerName}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Total Amount:</span>
                    <span className="font-medium">₹{order.totalAmount?.toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Amount Received:</span>
                    <span className="font-medium">₹{(order.amountReceived || 0).toLocaleString()}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300 flex justify-between">
                    <span>Balance to Pay:</span>
                    <span className={`font-medium ${currentBalance > 0 ? 'text-red-500 dark:text-red-400' : 'text-green-500 dark:text-green-400'}`}>
                      ₹{currentBalance.toLocaleString()}
                    </span>
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Additional Payment Amount
                    </label>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={formData.amountReceived}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '' || /^-?\d*\.?\d*$/.test(value) || value === '.' || value === '-') {
                          setFormData(prev => ({ 
                            ...prev, 
                            amountReceived: value 
                          }));
                        }
                      }}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 text-lg
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                        disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        notes: e.target.value 
                      }))}
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                        text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                        disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div className="mt-6 space-y-4">
                    <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                      <div className="flex justify-between items-center mb-4">
                        <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                        <span className="text-gray-900 dark:text-white">
                          ₹{calculateTotals().subtotal.toLocaleString()}
                        </span>
                      </div>

                      <div className="flex items-center space-x-4">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Discount
                        </label>
                        <input
                          type="text"
                          value={discount}
                          onChange={(e) => {
                            const value = e.target.value;
                            if (value === '' || /^\d*\.?\d*$/.test(value) || value === '.') {
                              setDiscount(value);
                            }
                          }}
                          onBlur={() => {
                            if (discount === '' || discount === '.') {
                              setDiscount('0');
                            } else {
                              setDiscount(Math.max(0, Number(discount) || 0).toString());
                            }
                          }}
                          className="w-32 px-4 py-3 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 
                            text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400
                            focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
                            disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed"
                          inputMode="decimal"
                        />
                      </div>

                      <div className="flex justify-between items-center mt-4 text-lg font-medium">
                        <span className="text-gray-900 dark:text-white">Total:</span>
                        <span className="text-gray-900 dark:text-white">
                          ₹{calculateTotals().total.toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                      <button
                        type="button"
                        onClick={onClose}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-gray-700 dark:text-gray-300 
                          bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg
                          hover:bg-gray-50 dark:hover:bg-gray-600 
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full sm:w-auto px-4 py-3 sm:py-2 text-base sm:text-sm font-medium text-white 
                          bg-primary-600 rounded-lg border border-transparent
                          hover:bg-primary-700 
                          focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent 
                          disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Updating...' : 'Update Order'}
                      </button>
                    </div>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}